/**
 * API client.
 *
 * Holds the access token in module-level state (memory only — never localStorage).
 * The refresh token lives in an HttpOnly cookie that the browser handles automatically.
 *
 * On a 401 with code TOKEN_EXPIRED, this client transparently:
 *   1. Calls /auth/refresh (cookie sent automatically)
 *   2. Stores the new access token
 *   3. Retries the original request once
 *
 * If the refresh fails, the access token is cleared and the AuthProvider
 * notices via the onAuthFailure callback and redirects to login.
 */

let _accessToken = null;
let _onAuthFailure = null;

// Track an in-flight refresh so concurrent requests share a single refresh call
let _refreshInFlight = null;

export function setAccessToken(token) {
    _accessToken = token;
}

export function getAccessToken() {
    return _accessToken;
}

export function clearAccessToken() {
    _accessToken = null;
}

export function setAuthFailureHandler(fn) {
    _onAuthFailure = fn;
}

class ApiError extends Error {
    constructor(status, code, message, details) {
        super(message);
        this.status = status;
        this.code = code;
        this.details = details;
    }
}

async function rawRequest(method, path, { body, retried, asBlob } = {}) {
    const headers = {};
    if (!asBlob) headers['Content-Type'] = 'application/json';
    if (_accessToken) headers['Authorization'] = `Bearer ${_accessToken}`;

    const res = await fetch(path, {
        method,
        headers,
        credentials: 'include', // send/receive cookies
        body: body ? JSON.stringify(body) : undefined,
    });

    // 204 No Content
    if (res.status === 204) return null;

    // Binary path — used for images. Failures still come back as JSON, so we
    // peek at content-type to decide how to parse the body.
    if (asBlob && res.ok) {
        return res.blob();
    }

    let data = null;
    const text = await res.text();
    if (text) {
        try { data = JSON.parse(text); }
        catch { data = text; }
    }

    if (res.ok) return data;

    // 401 with TOKEN_EXPIRED: try silent refresh + one retry
    if (
        res.status === 401 &&
        data?.error?.code === 'TOKEN_EXPIRED' &&
        !retried
    ) {
        const refreshed = await refreshAccessToken();
        if (refreshed) {
            return rawRequest(method, path, { body, retried: true, asBlob });
        }
    }

    const code = data?.error?.code || 'UNKNOWN';
    const msg = data?.error?.message || res.statusText;
    const details = data?.error?.details;
    throw new ApiError(res.status, code, msg, details);
}

async function refreshAccessToken() {
    if (_refreshInFlight) return _refreshInFlight;

    _refreshInFlight = (async () => {
        try {
            const res = await fetch('/api/v1/auth/refresh', {
                method: 'POST',
                credentials: 'include',
            });
            if (!res.ok) {
                clearAccessToken();
                if (_onAuthFailure) _onAuthFailure();
                return false;
            }
            const data = await res.json();
            _accessToken = data.token;
            // Notify other parts of the app (e.g., the Socket.io connection)
            // so they can update their auth credentials without reconnecting.
            try {
                window.dispatchEvent(new CustomEvent('starsapt:token-refreshed'));
            } catch (_) { /* SSR or test env — ignore */ }
            return true;
        } finally {
            _refreshInFlight = null;
        }
    })();

    return _refreshInFlight;
}

export const api = {
    get: (path) => rawRequest('GET', path),
    getBlob: (path) => rawRequest('GET', path, { asBlob: true }),
    post: (path, body) => rawRequest('POST', path, { body }),
    put: (path, body) => rawRequest('PUT', path, { body }),
    patch: (path, body) => rawRequest('PATCH', path, { body }),
    del: (path) => rawRequest('DELETE', path),
};

export { ApiError };
